import { computed, effect, Injectable, OnDestroy, signal } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LoadingBarService implements OnDestroy {
  private readonly isLoading = signal(false);
  private readonly disableSlowAPIButtons = signal(false);
  private readonly disableFastAPIButtons = signal(false);

  private slowTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private fastTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private activeRequests = new Set<string>();

  getIsLoading = computed(() => this.isLoading());
  getDisableSlowAPIButtons = computed(() => this.disableSlowAPIButtons());
  getDisableFastAPIButtons = computed(() => this.disableFastAPIButtons());

  constructor(private router: Router) {
    this.setupNavigationListener();
  }

  private setupNavigationListener(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationStart || event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError)).subscribe((event) => {
      const navId = '__navigation__';
      if (event instanceof NavigationStart) {
        this.startLoading(navId);
      } else {
        // NavigationEnd, NavigationCancel, NavigationError
        this.stopLoading(navId);
      }
    });
  }

  /**
   * Call this with a unique ID when a request starts.
   */
  public startLoading(id: string): void {
    const wasEmpty = this.activeRequests.size === 0;
    this.activeRequests.add(id);
    if (wasEmpty) {
      this.isLoading.set(true);
      this.handleButtonTimers(true);
    }
  }

  /**
   * Call this with the same ID when a request finishes.
   */
  public stopLoading(id: string): void {
    this.activeRequests.delete(id);
    if (this.activeRequests.size === 0) {
      this.isLoading.set(false);
      this.handleButtonTimers(false);
    }
  }

  private handleButtonTimers(isLoading: boolean): void {
    if (isLoading) {
      this.disableSlowAPIButtons.set(true);
      this.disableFastAPIButtons.set(true);
      if (this.slowTimeoutId) {
        clearTimeout(this.slowTimeoutId);
        this.slowTimeoutId = null;
      }
      if (this.fastTimeoutId) {
        clearTimeout(this.fastTimeoutId);
        this.fastTimeoutId = null;
      }
    } else {
      // Only start countdown if no requests are loading
      this.slowTimeoutId = setTimeout(() => {
        this.disableSlowAPIButtons.set(false);
        this.slowTimeoutId = null;
      }, 3000);

      this.fastTimeoutId = setTimeout(() => {
        this.disableFastAPIButtons.set(false);
        this.fastTimeoutId = null;
      }, 250);
    }
  }

  ngOnDestroy(): void {
    if (this.slowTimeoutId) {
      clearTimeout(this.slowTimeoutId);
    }
    if (this.fastTimeoutId) {
      clearTimeout(this.fastTimeoutId);
    }
  }
}
